/**
 * Provides a shared loading state for form submissions.
 * Standard form posts rely on the submit event/prototype patch.
 * Async handlers can call window.ThemeFormLoading.stop(form) when they finish.
 */
(function initializeFormSubmissionLoading() {
  if (window.ThemeFormLoading) {
    return;
  }

  /**
   * @typedef {HTMLButtonElement | HTMLInputElement} SubmitControl
   */

  /**
   * @param {Element} element
   * @returns {element is SubmitControl}
   */
  function isSubmitControl(element) {
    return (
      (element instanceof HTMLButtonElement && element.type === 'submit') ||
      (element instanceof HTMLInputElement && ['submit', 'image'].includes(element.type))
    );
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {SubmitControl[]}
   */
  function getSubmitControls(form) {
    return Array.from(form.elements).filter((element) => {
      return element instanceof Element && isSubmitControl(element);
    });
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  function usesManualMode(form) {
    const manualModeHost = form.closest('[data-form-loading-mode]');
    return manualModeHost instanceof HTMLElement && manualModeHost.dataset.formLoadingMode === 'manual';
  }

  /**
   * @param {HTMLFormElement} form
   * @returns {boolean}
   */
  function start(form) {
    if (!(form instanceof HTMLFormElement) || form.dataset.formLoading === 'true') {
      return false;
    }

    form.dataset.formLoading = 'true';
    form.setAttribute('aria-busy', 'true');

    for (const control of getSubmitControls(form)) {
      control.dataset.formLoadingWasDisabled = control.disabled ? 'true' : 'false';
      control.dataset.formLoading = 'true';
      control.style.setProperty('--form-loading-spinner-color', window.getComputedStyle(control).color);
      control.disabled = true;
    }

    return true;
  }

  /**
   * @param {HTMLFormElement} form
   */
  function stop(form) {
    if (!(form instanceof HTMLFormElement)) return;

    delete form.dataset.formLoading;
    form.removeAttribute('aria-busy');

    for (const control of getSubmitControls(form)) {
      const wasDisabled = control.dataset.formLoadingWasDisabled === 'true';
      control.disabled = wasDisabled;
      delete control.dataset.formLoading;
      delete control.dataset.formLoadingWasDisabled;
      control.style.removeProperty('--form-loading-spinner-color');
    }
  }

  function reset() {
    document.querySelectorAll('form[data-form-loading="true"]').forEach((form) => {
      if (form instanceof HTMLFormElement) {
        stop(form);
      }
    });
  }

  const nativeSubmit = HTMLFormElement.prototype.submit;

  HTMLFormElement.prototype.submit = function submitWithLoadingState() {
    if (!usesManualMode(this)) {
      start(this);
    }
    nativeSubmit.call(this);
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;

    if (event.defaultPrevented) {
      return;
    }

    if (form instanceof HTMLFormElement && !usesManualMode(form)) {
      start(form);
    }
  });

  window.addEventListener('pageshow', reset);

  window.ThemeFormLoading = {
    start,
    stop,
    reset,
    /**
     * @param {HTMLFormElement | null | undefined} form
     */
    submit(form) {
      if (!(form instanceof HTMLFormElement)) return;

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
        return;
      }

      if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
        return;
      }

      start(form);
      nativeSubmit.call(form);
    },
  };
})();
